import { createRouter, createWebHistory, createWebHashHistory } from 'vue-router';
import { routes } from './routes';
import { useAuthStore } from './stores/auth';
import { subscriptionService } from './services/subscriptionService';
import * as permissions from './utils/permissions';
import { hasRestaurantContext } from './utils/subdomain';
import { safeStorage } from './utils/storage';
import { isElectron } from './utils/subdomain';
import { isRouteAvailable } from './utils/platform';

// Cache para evitar llamadas API repetidas
let subscriptionCache: any = null;
let subscriptionCacheTime = 0;
let usageCache: any = null;
let usageCacheTime = 0;
const CACHE_DURATION = 30000; // 30 segundos

const router = createRouter({
  // Use hash history for Electron, web history for browser
  history: isElectron() ? createWebHashHistory() : createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 };
  }
});

/**
 * Funciones cacheadas para evitar llamadas API repetidas
 */
async function getCachedSubscriptionStatus() {
  const now = Date.now();
  if (subscriptionCache && (now - subscriptionCacheTime) < CACHE_DURATION) {
    return subscriptionCache;
  }
  
  try {
    const status = await subscriptionService.checkStatus();
    // Only cache if subscription is active; expired/blocked status must always be re-checked
    if (status.can_operate) {
      subscriptionCache = status;
      subscriptionCacheTime = now;
    } else {
      subscriptionCache = null;
      subscriptionCacheTime = 0;
    }
    return status;
  } catch (error) {
    // Si hay error, no cacheamos para reintentar en la próxima llamada
    throw error;
  }
}

async function getCachedUsage() {
  const now = Date.now();
  if (usageCache && (now - usageCacheTime) < CACHE_DURATION) {
    return usageCache;
  }
  
  try {
    const usage = await subscriptionService.getUsage();
    usageCache = usage;
    usageCacheTime = now;
    return usage;
  } catch (error) {
    // Si hay error, no cacheamos para reintentar en la próxima llamada
    throw error;
  }
}

/**
 * Global navigation guard with permission checks
 * Uses the centralized permissions module for consistent access control
 */
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const user = authStore.user;
  
  // 0. Check if route is available in current platform (Electron vs Web)
  if (to.name && !isRouteAvailable(to.name as string)) {
    // Redirect to Dashboard if trying to access cloud-only route in Electron
    next({ name: 'Dashboard' });
    return;
  }

  // 0.5. Check subscription status for authenticated non-sysadmin users
  // This check happens BEFORE any other checks to enforce subscription requirement
  if (authStore.isAuthenticated && user?.role !== 'sysadmin' && to.name !== 'Login' && to.name !== 'Register') {
    try {
      const subscriptionStatus = await getCachedSubscriptionStatus();
      
      // If no subscription or cannot operate, ONLY allow subscription page
      if (!subscriptionStatus.can_operate) {
        if (to.name !== 'Subscription') {
          next({ name: 'Subscription', replace: true });
          return;
        }
      }
    } catch (error: any) {
      // If API returns 403 (subscription error), force to subscription page
      if (error.response?.status === 403 && to.name !== 'Subscription') {
        next({ name: 'Subscription', replace: true });
        return;
      }
    }
  }

  // 1. Redirect root to login if not authenticated
  if (to.path === '/' && !authStore.isAuthenticated) {
    next({ name: 'Login', replace: true });
    return;
  }

  // 1. Check authentication requirement
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } });
    return;
  }

  // 2. Redirect authenticated users away from login/register
  if ((to.name === 'Login' || to.name === 'Register') && authStore.isAuthenticated) {
    next({ name: 'Dashboard' });
    return;
  }

  // 3. Check if route requires restaurant context (subdomain)
  if (to.meta.requiresRestaurantContext && !hasRestaurantContext()) {
    // If user is sysadmin, redirect to sysadmin dashboard
    if (user?.role === 'sysadmin') {
      next({ name: 'SysAdmin' });
    } else {
      // For other users, redirect to dashboard (which will show appropriate content)
      next({ name: 'Dashboard' });
    }
    return;
  }

  // 4. Check permission using permissions module
  if (to.meta.permissionCheck && typeof to.meta.permissionCheck === 'string') {
    const permissionFn = permissions[to.meta.permissionCheck as keyof typeof permissions];
    
    if (typeof permissionFn === 'function') {
      const hasPermission = permissionFn(user);
      
      if (!hasPermission) {
        // Redirect to Dashboard instead of Menu to avoid lazy loading issues
        next({ name: 'Dashboard' });
        return;
      }
    }
  }

  // 5. Check kitchen module subscription (if required)
  if (to.meta.requiresKitchenModule) {
    if (!authStore.isAuthenticated) {
      next({ name: 'Login', query: { redirect: to.fullPath } });
      return;
    }

    try {
      const usage = await getCachedUsage();
      
      if (!usage.has_subscription || !usage.features?.has_kitchen_module) {
        next({ name: 'Subscription', replace: true });
        return;
      }
    } catch (error) {
      next({ name: 'Subscription', replace: true });
      return;
    }
  }

  // 6. Check POS mode requirement (if required)
  if (to.meta.requiresPOSMode) {
    if (!authStore.isAuthenticated) {
      next({ name: 'Login', query: { redirect: to.fullPath } });
      return;
    }

    try {
      const usage = await getCachedUsage();
      
      // Check if operation mode is POS (not full_restaurant)
      const isPOSMode = usage.operation_mode === 'pos_only';
      
      if (!isPOSMode) {
        next({ name: 'Orders' }); // Redirect to regular orders view
        return;
      }
    } catch (error) {
      next({ name: 'Dashboard' });
      return;
    }
  }

  // 7. All checks passed, proceed
  next();
});

export default router;
